import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';

import { cn } from './cn';
import { Icon } from './Icon';
import { Text } from './Typography';

// Multi-select selection control. Closed and data-driven — siblings to
// RadioGroup; same option shape ({ value, label, description?,
// disabled? }), different value shape (array vs scalar).

const ITEM_INDICATOR_CLASSNAME = 'tw-peer tw-h-4 tw-w-4 tw-shrink-0 tw-rounded-sm tw-border tw-border-primary tw-ring-offset-background focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 data-[state=checked]:tw-bg-primary data-[state=checked]:tw-text-primary-foreground';

const toggleValue = (values, value) => (
    values.includes(value)
        ? values.filter(entry => entry !== value)
        : [...values, value]
);

const CheckboxGroup = React.forwardRef(({
    className: _className,
    style: _style,
    onChange,
    options = [],
    values = []
}, ref) => {
    const groupId = React.useId();
    const handleToggle = value => {
        onChange?.(toggleValue(values, value));
    };

    return (
        <div ref={ref} role="group" className={cn('tw-grid tw-gap-2')}>
            {options.map(option => {
                const itemId = `${groupId}-${option.value}`;
                const checked = values.includes(option.value);

                return (
                    <div key={option.value} className="tw-flex tw-items-start tw-gap-2">
                        <span className="tw-flex tw-h-6 tw-items-center">
                            <CheckboxPrimitive.Root
                                id={itemId}
                                className={ITEM_INDICATOR_CLASSNAME}
                                checked={checked}
                                disabled={option.disabled}
                                onCheckedChange={() => handleToggle(option.value)}
                            >
                                <CheckboxPrimitive.Indicator className="tw-flex tw-items-center tw-justify-center tw-text-current">
                                    <Icon icon="check" size="small" tone="inherit" />
                                </CheckboxPrimitive.Indicator>
                            </CheckboxPrimitive.Root>
                        </span>
                        <label
                            htmlFor={itemId}
                            className={cn(
                                'tw-select-none',
                                option.disabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
                            )}
                        >
                            <Text tone={option.disabled ? 'muted' : 'foreground'} variant="body">
                                {option.label}
                            </Text>
                            {option.description ? (
                                <Text as="p" tone="muted" variant="body-sm">
                                    {option.description}
                                </Text>
                            ) : null}
                        </label>
                    </div>
                );
            })}
        </div>
    );
});
CheckboxGroup.displayName = 'CheckboxGroup';

export { CheckboxGroup };
